import React from "react";
import { ArrowTrendingUpIcon } from "@heroicons/react/24/outline";

const SubscriptionUsageTab = ({
  organization,
  getPlanDisplayName,
  formatUsage,
  getUsagePercentage,
  isOrgOwner,
  setActiveSubTab,
  SUB_TABS,
}) => {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-base font-semibold text-gray-900">Usage</h2>
        <p className="text-xs text-gray-500 mt-0.5">
          Current usage and limits for your plan
        </p>
      </div>
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-6 sm:p-8">
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="p-4 rounded-xl bg-gray-50/80 border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">
                Current Usage and Limits
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-white border border-gray-100 rounded-lg">
                  <span className="text-gray-600 font-medium">
                    Sign Requests:
                  </span>
                  <span className="font-bold text-gray-900">
                    {formatUsage(
                      organization?.usedAllowedSignRequests || 0,
                      organization?.allowedSignRequests || 0
                    )}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-white border border-gray-100 rounded-lg">
                  <span className="text-gray-600 font-medium">
                    Template Creation:
                  </span>
                  <span className="font-bold text-gray-900">
                    {formatUsage(
                      organization?.usedAllowedTemplateCreation || 0,
                      organization?.allowedTemplateCreation || 0
                    )}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-white border border-gray-100 rounded-lg">
                  <span className="text-gray-600 font-medium">Bulk Send:</span>
                  <span
                    className={`font-bold px-3 py-1 rounded-full text-sm ${
                      organization?.allowBulkSend
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {organization?.allowBulkSend ? "Enabled" : "Disabled"}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-white border border-gray-100 rounded-lg">
                  <span className="text-gray-600 font-medium">
                    Team Members:
                  </span>
                  <span className="font-bold text-gray-900">
                    {organization?.memberCount || 0}
                  </span>
                </div>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-gray-50/80 border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">
                Usage Progress
              </h3>
              <div className="space-y-4">
                {organization?.allowedSignRequests > 0 && (
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-900">
                        {getPlanDisplayName(organization?.planId) || "Plan"}
                      </span>
                      <span className="text-sm font-bold text-green-600">
                        {organization?.usedAllowedSignRequests || 0}/
                        {organization?.allowedSignRequests || 0}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-500 h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${getUsagePercentage(
                            organization?.usedAllowedSignRequests || 0,
                            organization?.allowedSignRequests || 0
                          )}%`,
                        }}
                      />
                    </div>
                    <div className="flex justify-between items-center pt-2">
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-500">
                          Pending Envelopes Remaining
                        </span>
                        <span className="text-lg font-bold text-green-600 mt-1">
                          {Math.max(
                            0,
                            (organization?.allowedSignRequests || 0) -
                              (organization?.usedAllowedSignRequests || 0)
                          )}
                        </span>
                      </div>
                      {isOrgOwner && (
                        <button
                          type="button"
                          onClick={() => setActiveSubTab(SUB_TABS.upgrade)}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-primary/90 transition-colors"
                        >
                          <ArrowTrendingUpIcon className="w-4 h-4" />
                          Add Extra Envelopes
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {organization?.allowedTemplateCreation > 0 && (
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">
                        Template Creation
                      </span>
                      <span className="text-sm font-medium text-gray-900">
                        {getUsagePercentage(
                          organization?.usedAllowedTemplateCreation || 0,
                          organization?.allowedTemplateCreation || 0
                        )}
                        %
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-primary h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${getUsagePercentage(
                            organization?.usedAllowedTemplateCreation || 0,
                            organization?.allowedTemplateCreation || 0
                          )}%`,
                        }}
                      />
                    </div>
                  </div>
                )}

                {organization?.allowedSignRequests < 0 &&
                  organization?.allowedTemplateCreation < 0 && (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg
                          className="w-8 h-8 text-green-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">
                        Unlimited Usage
                      </h4>
                      <p className="text-sm text-gray-600">
                        You have unlimited access to all features
                      </p>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionUsageTab;
